import * as React from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle, FileDown, Globe, Mail, Palette, Plus, UserPlus, Users, X
} from 'lucide-react'
import {
  useFormKeys, useInviteUser, useSetOperationsAccess, useUpdateFormKeyOrigins,
  useUsers, useUserSeats
} from '@/lib/queries.js'
import { useTenant } from '@/app/TenantProvider.jsx'
import { humanise } from '@/lib/format.js'
import { Button } from '@/components/ui/button.jsx'
import { Card, CardBody, CardHeader, CardTitle, DetailRow, PageHeader } from '@/components/ui/card.jsx'
import { Field, Input, Label } from '@/components/ui/field.jsx'
import { Alert, Spinner } from '@/components/ui/feedback.jsx'
import { CopyButton } from '@/components/CopyButton.jsx'
import { humanMessage } from '@/lib/errors.js'
import { BillingCard } from '@/app/Billing.jsx'
import { DepositCard, PublicProfileCard, PaymentsCard, SendingDomainCard } from './PaymentSettings.jsx'
import { NotificationPreferencesCard } from './NotificationSettings.jsx'

const API_BASE = import.meta.env.VITE_API_URL

/**
 * Settings (Phase 1b screen 9) — and the Starter onboarding funnel (§2.3).
 *
 * ── THIS SCREEN LOOKS LIKE SETTINGS AND IS ACTUALLY ACTIVATION ──────────────
 * A Starter tenant's entire loop starts when a form on their existing website
 * begins posting leads to MBOS. Until that happens the dashboard is an empty
 * board and the subscription is unearned. So this is the highest-leverage
 * surface in the phase, not a housekeeping page.
 *
 * ── THE CONSTRAINT THAT SHAPES THE DESIGN ───────────────────────────────────
 * The contractor is usually NOT the person who can edit their website. It is a
 * nephew, an agency, or a Wix login nobody has used in two years. A funnel that
 * assumes the logged-in user will paste the snippet themselves stalls for a
 * large fraction of customers.
 *
 * So the real deliverable is not the snippet — it is a forwardable instruction
 * set the contractor can hand to whoever does have access, without
 * understanding it themselves. Hence the order below: snippet, then the origin
 * question in plain language, then the email, then per-platform one-liners.
 */
export function Settings () {
  const { tenant, workspaceName, canAccessOperations } = useTenant()
  const { data: formKeys = [], isPending, error } = useFormKeys()

  const activeKey = formKeys.find(k => k.is_active) ?? formKeys[0]

  return (
    <>
      <PageHeader
        title="Settings"
        description={workspaceName}
      />

      {error && <Alert tone="danger" className="mb-4">{humanMessage(error)}</Alert>}

      <div className="space-y-6">
        {isPending
          ? <div className="flex justify-center py-16"><Spinner label="Loading settings" /></div>
          : activeKey
            ? <LeadFormSection formKey={activeKey} workspaceName={workspaceName} />
            : (
              <Alert tone="warn" title="No lead form key">
                This workspace has no active form key, so the embedded lead form
                cannot be set up. Contact support — keys are issued during
                provisioning.
              </Alert>
              )}

        {/* Phase 2 (item 16a). Sits above Branding because every card here is
            something a CUSTOMER sees, and below the lead form because a
            workspace with no leads has nothing to quote yet.

            Deliberately NOT beside BillingCard further down: that one is MBOS
            charging this tenant, these are the tenant charging their own
            customers. Phase 0b spent a document keeping those apart and the
            settings screen is the one place a contractor could confuse them. */}
        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-ink">Getting paid</h2>
            <p className="mt-1 text-sm text-ink-muted">
              How your customers pay you, what you ask for up front, and how they
              reach you.
            </p>
          </div>

          <PaymentsCard />
          <DepositCard />
          <PublicProfileCard />
          <SendingDomainCard />
        </section>

        {/* Phase 2 item 19. Its own section: this is about the CONTRACTOR
            hearing about their own workspace, not about what a customer
            sees — a different audience from "Getting paid" above. */}
        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-ink">Notifications</h2>
            <p className="mt-1 text-sm text-ink-muted">
              What you hear about, and how.
            </p>
          </div>
          <NotificationPreferencesCard />
        </section>

        <BrandingSection tenant={tenant} />
        <PlanSection tenant={tenant} />
        <WorkspaceUsersSection />

        {/* Phase 9 D9.2 — data export MOVED to Operations.
            It was here (Phase 2 item 21) because Settings was the only place
            to put it. It is business/portability data rather than workspace
            setup, and it is now behind the Operations grant, so a sales hire
            with the CRM cannot take the book of business with them. Left as a
            cross-link rather than silently disappearing: this is where anyone
            who used it will look first. */}
        <DataExportMovedCard canAccessOperations={canAccessOperations} />

        {/* Phase 0b. Sits after the plan section, which describes what the
            tier includes — this one is about paying for it. */}
        <BillingCard />
      </div>
    </>
  )
}

/**
 * Where data export used to be (Phase 9 D9.2).
 *
 * Two audiences, one card. An owner gets a working link. A member who has been
 * using this and no longer can gets told plainly who to ask, rather than
 * finding the card simply gone and assuming the feature was withdrawn.
 */
function DataExportMovedCard ({ canAccessOperations }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileDown className="size-4 text-ink-subtle" aria-hidden="true" />
          Export your data
        </CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="text-sm/relaxed text-ink-muted">
          Exporting your clients, jobs and invoices has moved to Operations,
          where the rest of your business records live. It is still free, still
          any time, still no questions asked.
        </p>
        {canAccessOperations
          ? (
            <Button asChild variant="secondary" size="sm">
              <Link to="/operations/data-export" data-testid="data-export-moved-link">
                Go to Data export
              </Link>
            </Button>
            )
          : (
            <p className="text-sm/relaxed text-ink-muted">
              Operations is not switched on for your login. The workspace owner
              can turn it on for you below, under Workspace users.
            </p>
            )}
      </CardBody>
    </Card>
  )
}

function WorkspaceUsersSection () {
  const { isOwner } = useTenant()
  const { data: users = [], isPending: usersPending, error: usersError } = useUsers()
  const { data: seats, isPending: seatsPending } = useUserSeats()
  const invite = useInviteUser()
  const setAccess = useSetOperationsAccess()
  const [email, setEmail] = React.useState('')
  const [firstName, setFirstName] = React.useState('')
  const [lastName, setLastName] = React.useState('')
  const [done, setDone] = React.useState(null)

  const pending = usersPending || seatsPending
  const remaining = seats?.remaining ?? 0

  // WHICH row is being written, from the mutation's own in-flight variables
  // rather than a second piece of state that could disagree with it. Without
  // this, `setAccess.isPending` is a single flag for the whole list, so
  // toggling one teammate greys out every other teammate's control at the same
  // time — invisible with two logins and obviously wrong with ten.
  const inFlight = setAccess.isPending ? setAccess.variables : null

  async function submit (event) {
    event.preventDefault()
    setDone(null)
    const target = email.trim()
    if (!target) return
    try {
      const result = await invite.mutateAsync({
        email: target,
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        role: 'member'
      })
      setDone(result.user.email)
      setEmail('')
      setFirstName('')
      setLastName('')
    } catch {}
  }

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold tracking-tight text-ink">Workspace users</h2>
        <p className="mt-1 text-sm/relaxed text-ink-muted">
          People who can sign in and work in this account. Everyone here gets the
          full pipeline. {isOwner
            ? 'Switch on Operations for anyone who also needs reporting, exports, document templates and payroll.'
            : 'Operations access is granted by the workspace owner.'}
        </p>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3">
          <div>
            <CardTitle>Team access</CardTitle>
            {seats && (
              <p className="mt-1 text-sm text-ink-muted">
                {seats.used} of {seats.limit} seats used
              </p>
            )}
          </div>
          <Users className="size-5 text-ink-subtle" aria-hidden="true" />
        </CardHeader>
        <CardBody className="space-y-4">
          {usersError && <Alert tone="danger">{humanMessage(usersError)}</Alert>}
          {invite.error && <Alert tone="danger">{humanMessage(invite.error)}</Alert>}
          {setAccess.error && <Alert tone="danger">{humanMessage(setAccess.error)}</Alert>}
          {done && <Alert tone="success">Invite sent to {done}.</Alert>}

          {pending
            ? <div className="flex justify-center py-8"><Spinner label="Loading users" /></div>
            : (
              <ul className="divide-y divide-line rounded-md border border-line" data-testid="workspace-users">
                {users.map(user => (
                  <li
                    key={user.id}
                    className="flex flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink">
                        {[user.first_name, user.last_name].filter(Boolean).join(' ') || user.email}
                      </p>
                      <p className="truncate text-xs text-ink-muted">{user.email}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="text-xs text-ink-subtle">
                        {user.role === 'owner' ? 'Owner' : humanise(user.role ?? 'member')}
                      </span>
                      <OperationsAccessControl
                        user={user}
                        isOwner={isOwner}
                        // The row currently being written, and what it is being
                        // written TO. Both matter — see the component.
                        pending={inFlight?.userId === user.id ? inFlight : null}
                        onChange={next => setAccess.mutate({
                          userId: user.id,
                          operationsAccess: next
                        })}
                      />
                    </div>
                  </li>
                ))}
              </ul>
              )}

          <form onSubmit={submit} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <Field
                id="invite-email"
                label="Email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
                disabled={invite.isPending || remaining <= 0}
                required
              />
              <Field
                id="invite-first-name"
                label="First name"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                autoComplete="given-name"
                disabled={invite.isPending || remaining <= 0}
              />
              <Field
                id="invite-last-name"
                label="Last name"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                autoComplete="family-name"
                disabled={invite.isPending || remaining <= 0}
              />
            </div>
            <Button
              type="submit"
              loading={invite.isPending}
              disabled={!email.trim() || invite.isPending || remaining <= 0}
            >
              <UserPlus /> Send invite
            </Button>
          </form>
        </CardBody>
      </Card>
    </section>
  )
}

/**
 * One teammate's Operations grant (Phase 9 D9.1).
 *
 * ── THREE RENDERINGS, AND EACH ONE IS A DIFFERENT TRUTH ─────────────────────
 *   · the OWNER's own row — a static label. Their access follows their role and
 *     there is no stored grant to move, so the API answers 409
 *     `operations_access_owner_implicit` if asked. Rendering a switch that
 *     cannot be switched would be a lie the server then has to correct.
 *   · a NON-OWNER looking at the list — a static Yes/No. They can see who has
 *     access (the list is already visible to them) but cannot change it; the
 *     route is owner-gated and would answer 403.
 *   · the OWNER looking at anyone else — the real control.
 *
 * ── A CHECKBOX, NOT A CUSTOM SWITCH ─────────────────────────────────────────
 * There is no Switch primitive in components/ui yet, and inventing one for a
 * single consumer would mean re-solving focus, keyboard and disabled states
 * that a native input already gets right. Wrapped in a label with a tap-height
 * target so it stays usable on a phone (D8).
 *
 * ── WHY IT SHOWS THE DESIRED STATE WHILE THE WRITE IS IN FLIGHT ─────────────
 * Found by dashboard/e2e/operations.spec.js, which could not check the box:
 * a CONTROLLED checkbox reads its state from the server payload, so between
 * the click and the refetch landing it snapped straight back to unchecked.
 * Playwright called that "clicking the checkbox did not change its state",
 * which is exactly what a human sees too — a switch that ignores you for a
 * round trip, on a control whose whole job is to be unambiguous about who can
 * see the money.
 *
 * So while THIS row's write is in flight it renders the value being written.
 * That is optimism with a short leash: `pending` comes from the mutation's own
 * variables, so it clears the moment the request settles, and a failure snaps
 * the box back to the truth with the error alert above the list explaining
 * why. Nothing is cached and nothing has to be reconciled.
 *
 * ── WHAT HAPPENS AFTER THE CLICK ────────────────────────────────────────────
 * Revocation bites on the target's VERY NEXT request — access is read live from
 * tenant_users per request (D24.6/C14) and never carried in their token, so
 * they do not keep it until they next sign in.
 *
 * @param {object} props
 * @param {{operationsAccess: boolean}|null} [props.pending] this row's in-flight
 *   write, or null. Per-row, never a list-wide "busy" flag.
 */
function OperationsAccessControl ({ user, isOwner, pending, onChange }) {
  // The server's answer, unless this row is mid-write, in which case the value
  // being written to it.
  const granted = pending ? pending.operationsAccess : user.operations_access
  const label = granted ? 'Yes' : 'No'

  if (user.operations_access_locked) {
    return (
      <span className="text-xs text-ink-subtle" title="The owner always has Operations access.">
        Operations: always
      </span>
    )
  }

  if (!isOwner) {
    return <span className="text-xs text-ink-subtle">Operations: {label}</span>
  }

  return (
    <label className="flex min-h-tap cursor-pointer items-center gap-2 text-xs text-ink-muted">
      <input
        type="checkbox"
        className="size-4 rounded border-line-strong accent-brand"
        checked={granted}
        // NOT disabled while in flight. The optimistic value above already
        // makes the click land visibly, and disabling mid-write would move the
        // focus ring off a control the user is still on. aria-busy says what is
        // happening without taking the control away.
        aria-busy={Boolean(pending) || undefined}
        onChange={event => onChange(event.target.checked)}
        data-testid={`operations-access-${user.email}`}
      />
      Operations
    </label>
  )
}

/* ==========================================================================
   The funnel
   ========================================================================== */

function LeadFormSection ({ formKey, workspaceName }) {
  const snippet = buildSnippet(formKey.key)
  const origins = formKey.allowed_origins ?? []
  const notConfigured = origins.length === 0

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold tracking-tight text-ink">Your lead form</h2>
        <p className="mt-1 text-sm/relaxed text-ink-muted">
          Put this on your website and every enquiry lands straight on your
          pipeline. You do not need a new website — this works on whatever you
          already have.
        </p>
      </div>

      {/* The single most important warning in the product. An unconfigured
          origin means the form is live, looks fine, and silently rejects every
          real submission (403 origin_not_allowed). The customer would have no
          way to discover that. */}
      {notConfigured && (
        <Alert tone="warn" title="One step left" data-testid="origin-warning">
          Add your website address below. Until you do, the form will be
          rejected when someone tries to use it — and neither you nor they
          would see why.
        </Alert>
      )}

      {/* ---- 1. the snippet ------------------------------------------- */}
      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3">
          <CardTitle>The code</CardTitle>
          <CopyButton value={snippet} size="sm" label="Copy code" data-testid="copy-snippet" />
        </CardHeader>
        <CardBody className="space-y-3">
          <pre
            className="max-h-64 overflow-auto rounded-md bg-surface-sunken p-3 font-mono text-xs leading-relaxed text-ink"
            data-testid="embed-snippet"
          >{snippet}</pre>
          <p className="text-xs text-ink-subtle">
            The key in this code is not a password — it can only create leads on
            your workspace, so it is safe to have on a public page.
          </p>
        </CardBody>
      </Card>

      {/* ---- 2. the origin, in plain language -------------------------- */}
      <OriginSection formKey={formKey} />

      {/* ---- 3. the forwardable email ---------------------------------- */}
      <AgencyEmailCard snippet={snippet} origins={origins} workspaceName={workspaceName} />

      {/* ---- 4. per-platform one-liners -------------------------------- */}
      <PlatformInstructions />
    </section>
  )
}

/**
 * "Where will this form live?" — the D3 capability, asked as a question a
 * contractor can answer rather than as an allowlist editor.
 *
 * The server canonicalises what gets typed (`example.com` becomes
 * `https://example.com`), which is what makes accepting human input safe here:
 * an entry that does not match exactly would be stored and then silently never
 * match a real submission.
 */
function OriginSection ({ formKey }) {
  const update = useUpdateFormKeyOrigins()
  const [value, setValue] = React.useState('')
  const [error, setError] = React.useState(null)
  const origins = formKey.allowed_origins ?? []

  async function add (event) {
    event.preventDefault()
    setError(null)
    const entry = value.trim()
    if (!entry) return
    try {
      await update.mutateAsync({ id: formKey.id, allowedOrigins: [...origins, entry] })
      setValue('')
    } catch (err) { setError(err) }
  }

  async function remove (origin) {
    setError(null)
    try {
      await update.mutateAsync({
        id: formKey.id,
        allowedOrigins: origins.filter(o => o !== origin)
      })
    } catch (err) { setError(err) }
  }

  return (
    <Card>
      <CardHeader><CardTitle>Where will this form live?</CardTitle></CardHeader>
      <CardBody className="space-y-4">
        <p className="text-sm/relaxed text-ink-muted">
          Enter the address of the website you are putting the form on. This
          stops anyone else from using your form key on a site that is not
          yours.
        </p>

        {error && <Alert tone="danger">{humanMessage(error)}</Alert>}

        {origins.length > 0 && (
          <ul className="space-y-2" data-testid="origin-list">
            {origins.map(origin => (
              <li
                key={origin}
                className="flex min-h-tap items-center gap-2 rounded-md border border-line bg-surface-sunken px-3 py-2"
              >
                <Globe className="size-4 shrink-0 text-ink-subtle" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate font-mono text-sm text-ink">{origin}</span>
                <Button
                  variant="ghost" size="icon-sm"
                  onClick={() => remove(origin)}
                  aria-label={`Remove ${origin}`}
                  disabled={update.isPending}
                >
                  <X />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <form onSubmit={add} className="flex flex-col gap-2 sm:flex-row">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="new-origin" className="sr-only">Website address</Label>
            <Input
              id="new-origin"
              value={value}
              onChange={e => setValue(e.target.value)}
              placeholder="yourcompany.com"
              autoCapitalize="none" autoCorrect="off" spellCheck={false}
              inputMode="url"
              data-testid="origin-input"
            />
          </div>
          <Button type="submit" loading={update.isPending} disabled={!value.trim()} data-testid="add-origin">
            <Plus /> Add
          </Button>
        </form>

        <p className="text-xs text-ink-subtle">
          Typing <code className="font-mono">yourcompany.com</code> is fine — we
          will tidy it up. Add both <code className="font-mono">www</code> and
          non-<code className="font-mono">www</code> versions if your site uses
          both.
        </p>
      </CardBody>
    </Card>
  )
}

/**
 * The forwardable email — the centrepiece of the funnel (§2.3).
 *
 * Written in the CONTRACTOR's voice, not MBOS's, because the recipient's
 * relationship is with the contractor. It has to stand alone: the person
 * opening it has no MBOS account, no context and no patience. So it says what
 * to paste, where it goes, what happens after, and how to confirm it worked.
 */
function AgencyEmailCard ({ snippet, origins, workspaceName }) {
  const email = buildAgencyEmail({ snippet, origins, workspaceName })

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3">
        <div>
          <CardTitle>Send it to whoever runs your website</CardTitle>
          <p className="mt-1 text-sm text-ink-muted">
            Most people do not edit their own site. Copy this and forward it on.
          </p>
        </div>
        <CopyButton value={email} size="sm" label="Copy email" data-testid="copy-email" />
      </CardHeader>
      <CardBody>
        {/* Height-capped and scrollable. The email embeds the whole snippet by
            design — it has to stand alone for someone with no MBOS account —
            but left unbounded it runs to several screens and buries the
            per-platform instructions below it. The contractor is meant to copy
            this, not read it. */}
        <div className="flex items-start gap-2 rounded-md bg-surface-sunken p-3">
          <Mail className="mt-0.5 size-4 shrink-0 text-ink-subtle" aria-hidden="true" />
          <pre
            className="max-h-72 min-w-0 flex-1 overflow-auto whitespace-pre-wrap font-sans text-sm leading-relaxed text-ink"
            data-testid="agency-email"
          >{email}</pre>
        </div>
      </CardBody>
    </Card>
  )
}

/**
 * Per-platform one-liners.
 *
 * Shown inline rather than behind a dropdown so a contractor can see their
 * platform is covered without hunting for it. The last entry is the catch-all
 * that points back at the email, which is platform-agnostic by construction.
 */
const PLATFORMS = [
  {
    name: 'Wix',
    steps: 'Editor → Add (+) → Embed Code → Embed HTML → paste → Apply, then Publish.'
  },
  {
    name: 'Squarespace',
    steps: 'Edit the page → Add Block → Code → paste → Apply, then Save.'
  },
  {
    name: 'GoDaddy',
    steps: 'Website Builder → Edit Site → Add Section → HTML → paste, then Publish.'
  },
  {
    name: 'WordPress',
    steps: 'Edit the page → add a Custom HTML block → paste, then Update. (Classic editor: the Text tab, not Visual.)'
  }
]

function PlatformInstructions () {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Where to paste it</CardTitle>
        <p className="mt-1 text-sm text-ink-muted">Find your website builder below.</p>
      </CardHeader>
      <CardBody className="p-0">
        <ul className="divide-y divide-line" data-testid="platform-list">
          {PLATFORMS.map(platform => (
            <li key={platform.name} className="px-4 py-3 sm:px-5">
              <p className="text-sm font-medium text-ink">{platform.name}</p>
              <p className="mt-0.5 text-sm/relaxed text-ink-muted">{platform.steps}</p>
            </li>
          ))}
          <li className="px-4 py-3 sm:px-5">
            <p className="text-sm font-medium text-ink">Something else, or an agency built it</p>
            <p className="mt-0.5 text-sm/relaxed text-ink-muted">
              Forward the email above — it works for any website, whoever built it.
            </p>
          </li>
        </ul>
      </CardBody>
    </Card>
  )
}

/* ==========================================================================
   Branding + plan
   ========================================================================== */

/**
 * Branding is READ-ONLY here (§2.2). Editing — logo upload, colour picker — is
 * Phase 5, where it is sold as part of white-label. Showing the current values
 * lets a tenant see what their workspace looks like and who to ask to change
 * it, without implying a control that does not exist.
 */
function BrandingSection ({ tenant }) {
  const branding = tenant.branding ?? {}
  const hasAny = Object.values(branding).some(Boolean)

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold tracking-tight text-ink">Branding</h2>
        <p className="mt-1 text-sm text-ink-muted">
          How your workspace looks. Contact us to change it.
        </p>
      </div>

      <Card>
        <CardBody>
          {!hasAny
            ? (
              <div className="flex items-start gap-2.5">
                <Palette className="mt-0.5 size-4 shrink-0 text-ink-subtle" aria-hidden="true" />
                <p className="text-sm text-ink-muted">
                  Using the standard MBOS look. Send us your logo and colours and
                  we will apply them.
                </p>
              </div>
              )
            : (
              <dl className="space-y-2.5">
                <DetailRow label="Business name">{branding.business_name ?? tenant.name}</DetailRow>
                <DetailRow label="Logo">
                  {branding.logo_url
                    ? <img src={branding.logo_url} alt="" className="h-8 max-w-[10rem] object-contain" />
                    : null}
                </DetailRow>
                <DetailRow label="Primary colour">
                  {branding.primary_color
                    ? (
                      <span className="flex items-center gap-2">
                        <span
                          className="size-4 rounded border border-line"
                          style={{ backgroundColor: branding.primary_color }}
                          aria-hidden="true"
                        />
                        <span className="font-mono text-xs" data-testid="branding-primary">
                          {branding.primary_color}
                        </span>
                      </span>
                      )
                    : null}
                </DetailRow>
                <DetailRow label="Accent colour">
                  {branding.secondary_color
                    ? (
                      <span className="flex items-center gap-2">
                        <span
                          className="size-4 rounded border border-line"
                          style={{ backgroundColor: branding.secondary_color }}
                          aria-hidden="true"
                        />
                        <span className="font-mono text-xs">{branding.secondary_color}</span>
                      </span>
                      )
                    : null}
                </DetailRow>
              </dl>
              )}
        </CardBody>
      </Card>
    </section>
  )
}

function PlanSection ({ tenant }) {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold tracking-tight text-ink">Plan</h2>
      </div>
      <Card>
        <CardBody>
          <dl className="space-y-2.5">
            <DetailRow label="Tier">
              <span data-testid="tenant-tier">{humanise(tenant.tier)}</span>
            </DetailRow>
            <DetailRow label="Status">{humanise(tenant.status)}</DetailRow>
            <DetailRow label="Workspace address">
              <span className="font-mono text-xs">{tenant.slug}</span>
            </DetailRow>
          </dl>
          {tenant.status === 'past_due' && (
            <Alert tone="warn" title="Payment overdue" className="mt-4">
              <span className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                Your subscription needs attention. Contact us to keep your
                workspace active.
              </span>
            </Alert>
          )}
        </CardBody>
      </Card>
    </section>
  )
}

/* ==========================================================================
   Content builders
   ========================================================================== */

/**
 * The embed snippet.
 *
 * A plain HTML form with no JavaScript, deliberately. A `<form>` POST with
 * `application/x-www-form-urlencoded` is a CORS "simple request" — it does not
 * trigger a preflight, which means it works on locked-down site builders that
 * strip scripts. The API's public router parses urlencoded bodies for exactly
 * this reason.
 *
 * The honeypot field mirrors what the API expects: filled in, it returns 200
 * and creates nothing, so a bot learns nothing.
 */
function buildSnippet (key) {
  return `<!-- ${'Lead form'} — sends enquiries straight to your MBOS pipeline -->
<form action="${API_BASE}/api/public/leads" method="post">
  <input type="hidden" name="form_key" value="${key}">

  <label>Name
    <input type="text" name="name" required>
  </label>

  <label>Email
    <input type="email" name="email">
  </label>

  <label>Phone
    <input type="tel" name="phone">
  </label>

  <label>What do you need?
    <textarea name="service_requested"></textarea>
  </label>

  <!-- Spam trap: leave this hidden and empty. -->
  <input type="text" name="website" style="display:none" tabindex="-1" autocomplete="off">

  <button type="submit">Send</button>
</form>`
}

function buildAgencyEmail ({ snippet, origins, workspaceName }) {
  const site = origins[0] ?? 'our website'
  return `Hi,

We've moved our enquiries over to a new system, and I need the contact form on ${site} to send to it.

Could you please add the code below to our website? It can go on the contact page, or anywhere a form makes sense.

It's a plain HTML form — no scripts, no accounts to set up, and nothing to configure. Paste it in and publish.

--- START ---

${snippet}

--- END ---

Once it's live, could you send a quick test enquiry so I can confirm it arrives? I'll see it come through on my end straight away.

If your page builder needs the form somewhere specific, or you hit any trouble, let me know and I'll get it sorted.

Thanks,
${workspaceName}`
}
